export type ContentEditableSelectionSnapshot = {
  startPath: number[]
  startOffset: number
  endPath: number[]
  endOffset: number
}

const getNodePath = (root: HTMLElement, node: Node): number[] | null => {
  const path: number[] = []
  let current: Node | null = node

  while (current !== null && current !== root) {
    const parent: ParentNode | null = current.parentNode
    if (parent === null) return null

    path.unshift(Array.prototype.indexOf.call(parent.childNodes, current))
    current = parent
  }

  return current === root ? path : null
}

const resolveNodePath = (root: HTMLElement, path: number[]): Node | null => {
  let current: Node = root

  for (const index of path) {
    const child: ChildNode | null = current.childNodes.item(index)
    if (child === null) return null
    current = child
  }

  return current
}

export const snapshotContentEditableSelection = (
  root: HTMLElement | null | undefined,
): ContentEditableSelectionSnapshot | null => {
  if (!root) return null

  const selection = window.getSelection()
  if (!selection?.rangeCount) return null

  const range = selection.getRangeAt(0)
  if (!root.contains(range.commonAncestorContainer)) return null

  const startPath = getNodePath(root, range.startContainer)
  const endPath = getNodePath(root, range.endContainer)
  if (!startPath || !endPath) return null

  return {
    startPath,
    startOffset: range.startOffset,
    endPath,
    endOffset: range.endOffset,
  }
}

export const restoreContentEditableSelection = (
  root: HTMLElement | null | undefined,
  snapshot: ContentEditableSelectionSnapshot | null,
): boolean => {
  if (!root || !snapshot) return false

  const startContainer = resolveNodePath(root, snapshot.startPath)
  const endContainer = resolveNodePath(root, snapshot.endPath)
  if (!startContainer || !endContainer) return false

  try {
    const range = document.createRange()
    range.setStart(startContainer, snapshot.startOffset)
    range.setEnd(endContainer, snapshot.endOffset)
    root.focus({ preventScroll: true })
    const selection = window.getSelection()
    selection?.removeAllRanges()
    selection?.addRange(range)
    return true
  } catch {
    return false
  }
}

export const getFlatCaretOffset = (editor: HTMLElement): number | null => {
  const selection = window.getSelection()
  if (!selection?.rangeCount) return null

  const range = selection.getRangeAt(0)
  if (!editor.contains(range.startContainer)) return null

  const preRange = document.createRange()
  preRange.selectNodeContents(editor)
  preRange.setEnd(range.startContainer, range.startOffset)

  return preRange.toString().length
}

export const placeCaretAtFlatOffset = (
  editor: HTMLElement | null | undefined,
  offset: number | null,
): boolean => {
  if (!editor || offset === null || offset < 0) return false

  const totalLength = editor.textContent?.length ?? 0
  const targetOffset = Math.min(offset, totalLength)

  let remaining = targetOffset
  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT)
  let textNode: Text | null = walker.nextNode() as Text | null
  let lastTextNode: Text | null = null

  while (textNode) {
    lastTextNode = textNode
    const length = textNode.data.length
    if (remaining <= length) {
      try {
        const range = document.createRange()
        range.setStart(textNode, remaining)
        range.collapse(true)
        editor.focus({ preventScroll: true })
        const selection = window.getSelection()
        selection?.removeAllRanges()
        selection?.addRange(range)
        return true
      } catch {
        return false
      }
    }
    remaining -= length
    textNode = walker.nextNode() as Text | null
  }

  if (lastTextNode) {
    try {
      const range = document.createRange()
      range.setStart(lastTextNode, lastTextNode.data.length)
      range.collapse(true)
      editor.focus({ preventScroll: true })
      const selection = window.getSelection()
      selection?.removeAllRanges()
      selection?.addRange(range)
      return true
    } catch {
      return false
    }
  }

  placeCaretAtEnd(editor)
  return true
}

export function placeCaretAtEnd (el?: HTMLElement): void {
  if (!el) {
    return
  }
  el.focus()
  if (typeof window.getSelection !== 'undefined' &&
    typeof document.createRange !== 'undefined') {
    const range = document.createRange()
    range.selectNodeContents(el)
    range.collapse(false)
    const sel = window.getSelection()
    if (sel) {
      sel.removeAllRanges()
      sel.addRange(range)
    }
  }
}
